import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { DaeMahngJiPattern } from '../../components/types';
import { BeltRank } from "../../MembersArea";
import { DmjPatternBody } from "./DaeMahngJiPatternBody";
import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface DaeMahngJiProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  patterns: DaeMahngJiPattern[];
  currentPattern: number;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

export default function DaeMahngJi({
  selectedBelt,
  getBeltColor: _getBeltColor,
  patterns,
  currentPattern,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: DaeMahngJiProps) {
  if (patterns.length === 0) return null;

  const getGradingRequirements = (belt: BeltRank) => {
    switch (belt) {
      case 'GREEN':
        return 'DMJ 1';
      case 'PURPLE':
        return 'DMJ 1-2 Combo';
      case 'BROWN':
        return 'DMJ 1-2 Combo, & 3';
      case 'RED':
        return 'DMJ 1-2 Combo & 3-4 Combo';
      case 'NAVY':
        return 'DMJ 1-5 Combo';
      case 'BLACK/WHITE':
      case 'BLACK/D1':
      case 'BLACK/D2':
      case 'BLACK/D3':
      case 'BLACK/D4':
      case 'BLACK/D5':
        return 'DMJ 1-6 Combo';
      default:
        return '';
    }
  };

  const fingerprint = patterns.map((p) => p.number).join("|");
  const [activeTab, setActiveTab] = useState(() => String(patterns[0]?.number ?? 1));

  useEffect(() => {
    const nums = patterns.map((p) => p.number);
    const first = nums[0] ?? 1;
    if (presentation === "curriculum") {
      setActiveTab(String(first));
      return;
    }
    const pref =
      currentPattern && nums.includes(currentPattern) ? currentPattern : first;
    setActiveTab(String(pref));
  }, [fingerprint, currentPattern, presentation]);

  if (presentation === "curriculum") {
    return (
      <div className="space-y-4 text-left">
        <h2 className="text-xl font-semibold text-taekwondo-red md:text-2xl">
          DaeMahngJi - Big Dream Earth
        </h2>
        <div>
          <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Required for Grading</h3>
          <div className="rounded-lg bg-gray-50 p-4">
            <p className="text-gray-700">{getGradingRequirements(selectedBelt)}</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
          <ScrollableCurriculumTabStrip key={fingerprint} ariaLabelPrefix="DaeMahngJi patterns">
            {patterns.map((p) => (
              <TabsTrigger
                key={p.number}
                value={String(p.number)}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-4 py-2 text-sm font-medium tabular-nums transition-shadow",
                  beltTabSurfaceClass,
                  "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                )}
              >
                {p.number}
              </TabsTrigger>
            ))}
          </ScrollableCurriculumTabStrip>

          {patterns.map((p) => (
            <TabsContent
              key={p.number}
              value={String(p.number)}
              className="mt-0 focus-visible:outline-none"
            >
              <div className="mt-2 rounded-lg bg-gray-50 p-4 md:p-5">
                <DmjPatternBody pattern={p} hasDemo={hasDemo} onWatchDemo={onWatchDemo} />
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    );
  }

  return (
    <div className="space-y-4 ">
      {/* Grading Requirements Section */}
      <div>
        <h3 className="font-semibold text-lg mb-2 text-taekwondo-red pt-2">Required for Grading</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
        <p className="text-gray-700">{getGradingRequirements(selectedBelt)}</p>
        </div>
      </div>

      <Accordion type="single" collapsible className="w-full">
        {patterns.map((pattern) => (
          <AccordionItem 
            key={`dmj-pattern-${pattern.number}`}
            value={`dmj-pattern-${pattern.number}`}
          >
            <AccordionTrigger 
              className={`hover:no-underline ${pattern.number === currentPattern ? 'font-semibold text-taekwondo-blue' : ''}`}
            >
              <div className="flex items-center w-full text-left">
                <span className="flex-1">
                  {pattern.name} ({pattern.meaning})
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <DmjPatternBody pattern={pattern} hasDemo={hasDemo} onWatchDemo={onWatchDemo} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
} 